'use client'

import { useCallback } from 'react'
import { usePathname } from 'next/navigation'
import GiftIcon from 'public/static/campaigns/gift.svg'

import { useEndCustomerSession } from 'composition/session/useEndCustomerSession'
import { useQueryRewardsProgram } from 'modules/content'
import { useDeliverySupport } from 'modules/delivery'
import Button from 'shared/ui/Button'
import LinkButton from 'shared/ui/Button/LinkButton'
import { isDateInRange } from 'shared/utils/date'
import { PATHS } from 'shared/utils/paths'

import styles from './MenuItemsList.module.scss'

const profileLinks = [
  { id: 'profile_orders', path: PATHS.profileOrders, label: 'Zamówienia' },
  {
    id: 'profile_rebate_codes',
    path: PATHS.profileRebateCodes,
    label: 'Kody rabatowe'
  },
  { id: 'profile_my_details', path: PATHS.profileDetails, label: 'Moje dane' },
  {
    id: 'profile_delivery_addresses',
    path: PATHS.profileDeliveryAddress,
    label: 'Adresy dostaw'
  },
  {
    id: 'profile_invoice_addresses',
    path: PATHS.profileInvoiceDetails,
    label: 'Dane do faktury'
  },
  { id: 'profile_favorites', path: PATHS.profileFavourites, label: 'Ulubione' }
]

interface MenuItemsListProps {
  onClose?: () => void
}

const MenuItemsList = ({ onClose }: MenuItemsListProps) => {
  const pathname = usePathname()
  const { clearPostalCode } = useDeliverySupport()
  const logout = useEndCustomerSession()
  const { data: campaignInfo } = useQueryRewardsProgram()

  const handleClose = useCallback(() => {
    onClose && onClose()
  }, [onClose])

  const handleLogout = useCallback(
    () =>
      logout(() => {
        clearPostalCode()
        handleClose()
      }),
    [logout, clearPostalCode, handleClose]
  )

  return (
    <div className={styles.wrapper}>
      <nav className={styles.navigation} aria-label='Profil użytkownika'>
        <ul className={styles.list}>
          {campaignInfo?.isEnabled?.booleanField &&
            isDateInRange(campaignInfo.range) && (
              <li className={styles.item}>
                <LinkButton
                  className={styles.campaignLinkButton}
                  id='profile_shopping_that_matters_campaign'
                  active={pathname === PATHS.profileRewardsProgram}
                  href={PATHS.profileRewardsProgram}
                  onClick={handleClose}
                  aria-current={
                    pathname === PATHS.profileRewardsProgram
                      ? 'page'
                      : undefined
                  }
                  variant='neutralOutline'
                  size='large'
                  fullWidth
                >
                  <GiftIcon />

                  {campaignInfo.header}
                </LinkButton>
              </li>
            )}
          {profileLinks.map(({ id, path, label }) => {
            const isActive =
              pathname === path || pathname.startsWith(path + '/')

            return (
              <li className={styles.item} key={id}>
                <LinkButton
                  id={id}
                  active={isActive}
                  href={path}
                  onClick={handleClose}
                  aria-current={isActive ? 'page' : undefined}
                  variant='neutralOutline'
                  size='large'
                  fullWidth
                >
                  {label}
                </LinkButton>
              </li>
            )
          })}
        </ul>
      </nav>

      <Button
        id='profile_logout_button'
        onClick={handleLogout}
        variant='neutralOutline'
        size='large'
        fullWidth
      >
        Wyloguj się
      </Button>
    </div>
  )
}

export default MenuItemsList
